import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Flag } from 'lucide-react';
import { ACTIVITY_TYPE_ICONS, ACTIVITY_TYPE_LABELS } from '../crm/Leads/activityConstants';
import { CALENDAR_ITEM_COLORS } from './calendarConstants';
import { tint } from '../../shared/Pill';

// One Meeting/Call/Task on the calendar (issue #229). Clicking navigates to
// the Commercial Entity the item belongs to, with that specific Activity
// row pre-expanded — see CommercialEntityDetailPage.js's own
// `location.state?.openActivityId` effect. A Task chip is also draggable to
// a different day, to change its Due Date — Meetings/Calls aren't, since
// their date means "scheduled" rather than "due" (same reasoning as
// activityController.rescheduleTask's own scoping).
// A Trade Show with a date, on each day it runs. Not draggable: its dates
// are the show's, not ours. Clicking opens Trade Shows.
function TradeShowChip({ item }) {
  const navigate = useNavigate();
  const color = CALENDAR_ITEM_COLORS.trade_show;
  return (
    <div
      onClick={(e) => { e.stopPropagation(); navigate('/market-intel-trade-shows'); }}
      title={`Trade Show — ${item.name}${item.dateRange ? ` (${item.dateRange})` : ''}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 11,
        fontWeight: 600,
        color: '#fff',
        background: color,
        borderRadius: 4,
        padding: '2px 5px',
        cursor: 'pointer',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      }}
    >
      <Flag size={11} style={{ flexShrink: 0 }} />
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{item.name}</span>
    </div>
  );
}

function CalendarItemChip({ item, isDragging, onDragStartTask, onDragEndTask }) {
  if (item.kind === 'trade_show') return <TradeShowChip item={item} />;
  return <ActivityChip item={item} isDragging={isDragging} onDragStartTask={onDragStartTask} onDragEndTask={onDragEndTask} />;
}

function ActivityChip({ item, isDragging, onDragStartTask, onDragEndTask }) {
  const navigate = useNavigate();
  const Icon = ACTIVITY_TYPE_ICONS[item.activity_type];
  const color = CALENDAR_ITEM_COLORS[item.activity_type] || '#5a6b85';
  const label = item.topic || ACTIVITY_TYPE_LABELS[item.activity_type] || item.activity_type;
  const isTask = item.activity_type === 'task';
  const isDoneTask = isTask && item.is_done;

  // An Activity attached to an Individual reads on the calendar as
  // belonging to that person alone. When they're currently linked to an
  // Organisation (or several — e.g. a shared/contract contact), lead with
  // that Org context instead, since that's the commercial relationship the
  // Activity is really about — "Send pricelist (Factory Sound — John)"
  // rather than just "(John)". Falls back to the person's own name when
  // they have no current Org link, and is unaffected when the Activity is
  // attached directly to an Organisation.
  const entity = item.commercial_entity;
  const linkedOrgNames = entity?.type === 'individual' ? entity.linked_organisations : null;
  const entityLabel = linkedOrgNames?.length ? `${linkedOrgNames.join(', ')} — ${entity.name}` : entity?.name;

  const handleClick = (e) => {
    e.stopPropagation();
    if (!item.commercial_entity_id) return;
    navigate(`/commercial-entities/${item.commercial_entity_id}`, { state: { openActivityId: item.id } });
  };

  return (
    <div
      draggable={isTask}
      onDragStart={isTask ? () => onDragStartTask(item.id) : undefined}
      onDragEnd={isTask ? onDragEndTask : undefined}
      onClick={handleClick}
      title={`${ACTIVITY_TYPE_LABELS[item.activity_type] || item.activity_type}${item.topic ? ` — ${item.topic}` : ''}${entityLabel ? ` (${entityLabel})` : ''}${isTask ? ' — drag to change Due Date' : ''}`}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 4,
        fontSize: 11,
        fontWeight: 600,
        color,
        background: tint(color),
        borderRadius: 4,
        padding: '2px 5px',
        cursor: isTask ? 'grab' : 'pointer',
        opacity: isDragging ? 0.4 : 1,
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
      }}
    >
      {Icon && <Icon size={11} style={{ flexShrink: 0 }} />}
      {item.start_time && <span style={{ flexShrink: 0 }}>{item.start_time.slice(0, 5)}</span>}
      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', textDecoration: isDoneTask ? 'line-through' : 'none', textDecorationThickness: isDoneTask ? '2px' : undefined }}>{label}</span>
    </div>
  );
}

export default CalendarItemChip;
